import type { FieldErrors } from '../../../lib/validate';
import { IssueList, ListEditor, NumberInput, SelectInput, inputCls, smallBtn } from '../fields';
import type { Option } from '../refs';

/**
 * A dictionary (`Record<string, number | string>`) edited as key/value rows with add and remove. `keys` offers suggested keys
 * (a select per row, an unknown current key stays selectable); without it the key is free text. Errors come at `base` and
 * `base.<key>`.
 */
export function KeyValueEditor<V extends number | string>({
  label,
  value,
  onChange,
  errors,
  base,
  kind,
  keys,
  hint,
  newValue,
  multiline,
}: {
  label: string;
  value: Record<string, V> | undefined | null;
  onChange: (next: Record<string, V>) => void;
  errors: FieldErrors;
  base: string;
  kind: 'number' | 'string';
  keys?: readonly string[];
  hint?: string;
  newValue?: V;
  multiline?: boolean;
}) {
  const entries = Object.entries(value ?? {});
  const write = (rows: [string, V][]) => onChange(Object.fromEntries(rows) as Record<string, V>);
  const fresh = (newValue ?? (kind === 'number' ? 0 : '')) as V;
  const nextKey = () => {
    const taken = new Set(entries.map(([k]) => k));
    const free = keys?.find((k) => !taken.has(k));
    if (free) return free;
    let n = entries.length + 1;
    while (taken.has(`key_${n}`)) n++;
    return keys ? '' : `key_${n}`;
  };
  const options: Option[] = (keys ?? []).map((k) => ({ value: k, label: k }));
  return (
    <div className="sm:col-span-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
        <button
          type="button"
          className={smallBtn}
          onClick={() => {
            const k = nextKey();
            if (entries.some(([e]) => e === k)) return;
            write([...entries, [k, fresh]]);
          }}
        >
          Add {label.toLowerCase()} row
        </button>
      </div>
      {hint && <p className="text-xs text-bruise">{hint}</p>}
      {entries.length === 0 && <p className="text-xs text-bruise">None.</p>}
      <ul className="mt-1 space-y-1">
        {entries.map(([k, v], i) => (
          <li key={i} className="flex items-start gap-2 rounded border border-newsprint p-1">
            <div className="w-44 shrink-0">
              {keys ? (
                <SelectInput
                  label={`${label} key ${i + 1}`}
                  value={k}
                  options={options}
                  onChange={(nk) =>
                    write(entries.map((e, j) => (j === i ? [nk ?? '', e[1]] : e)) as [string, V][])
                  }
                />
              ) : (
                <input
                  aria-label={`${label} key ${i + 1}`}
                  className={`${inputCls} font-mono`}
                  value={k}
                  onChange={(e) =>
                    write(
                      entries.map((x, j) => (j === i ? [e.target.value, x[1]] : x)) as [
                        string,
                        V,
                      ][],
                    )
                  }
                />
              )}
            </div>
            <div className="min-w-0 flex-1">
              {kind === 'number' ? (
                <NumberInput
                  label={`${label} value ${i + 1}`}
                  value={v as number}
                  onChange={(n) =>
                    write(
                      entries.map((x, j) => (j === i ? [x[0], (n ?? 0) as V] : x)) as [string, V][],
                    )
                  }
                />
              ) : multiline ? (
                <textarea
                  aria-label={`${label} value ${i + 1}`}
                  className={`${inputCls} h-16`}
                  value={v as string}
                  onChange={(e) =>
                    write(
                      entries.map((x, j) => (j === i ? [x[0], e.target.value as V] : x)) as [
                        string,
                        V,
                      ][],
                    )
                  }
                />
              ) : (
                <input
                  aria-label={`${label} value ${i + 1}`}
                  className={inputCls}
                  value={v as string}
                  onChange={(e) =>
                    write(
                      entries.map((x, j) => (j === i ? [x[0], e.target.value as V] : x)) as [
                        string,
                        V,
                      ][],
                    )
                  }
                />
              )}
              <IssueList issues={errors[`${base}.${k}`]} />
            </div>
            <button
              type="button"
              className={`${smallBtn} text-blam`}
              aria-label={`Remove ${label} row ${i + 1}`}
              onClick={() => write(entries.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
      <IssueList issues={errors[base]} />
    </div>
  );
}

/** An ordered list of ids, each picked from `options` (an unknown current id stays selectable), with reorder. */
export function IdListEditor({
  label,
  value,
  onChange,
  errors,
  base,
  options,
  hint,
  what,
}: {
  label: string;
  value: string[] | undefined | null;
  onChange: (next: string[]) => void;
  errors: FieldErrors;
  base: string;
  options: Option[];
  hint?: string;
  what?: string;
}) {
  return (
    <ListEditor
      label={label}
      hint={hint}
      what={what ?? 'entry'}
      items={value ?? []}
      issues={errors[base]}
      onChange={onChange}
      make={() => ''}
      row={(id, j, set) => (
        <>
          <SelectInput
            label={`${label} ${j + 1}`}
            value={id}
            options={options}
            invalid={!!errors[`${base}.${j}`]?.some((i) => i.severity === 'error')}
            onChange={(v) => set(v ?? '')}
          />
          <IssueList issues={errors[`${base}.${j}`]} />
        </>
      )}
    />
  );
}
